import { useEffect } from "react";

/**
 * Scroll horizontal de tablas sin barra visible (regla de DESIGN de Eduardo, 2026-10-19).
 *
 * La barra gris de 8 px se cortaba contra el borde redondeado de la tarjeta y no le gustaba, pero
 * sin barra no se nota que hay más columnas. Este vigilante, llamado una sola vez en `App`, marca
 * cada contenedor de scroll que envuelve directamente un `<table>` con `data-desborde` (`izq`,
 * `der` o `ambos`) según el lado donde queda contenido oculto, y `index.css` dibuja con eso un
 * degradado en ese borde (mask-image, ver "Scroll horizontal de tablas"). La barra la oculta solo
 * el CSS: si este script no corre, la tabla se sigue desplazando y solo falta el degradado.
 *
 * Por qué JS y no solo CSS: el truco de fondos `background-attachment: local/scroll` pinta debajo
 * del contenido, y el encabezado gris (`--muted`) y la columna anclada (`.data-table--anclada`)
 * son opacos: la sombra no se veía en el encabezado ni sobre la columna fija. La máscara va encima
 * de todo y no agrega nodos al árbol de React.
 *
 * Desplazamiento: trackpad, shift+rueda y touch son nativos aunque la barra no se vea. Para
 * teclado, el contenedor que desborda y no es enfocable recibe `tabindex=0` (flechas lo mueven).
 */

export type Desborde = "izq" | "der" | "ambos" | null;

/** Lado(s) con contenido oculto. Tolera 1 px por el redondeo de `scrollLeft` con zoom. */
export function calcularDesborde(scrollLeft: number, scrollWidth: number, clientWidth: number): Desborde {
  const oculto = scrollWidth - clientWidth;
  if (oculto <= 1) return null;
  const x = Math.abs(scrollLeft);
  const izq = x > 1;
  const der = x < oculto - 1;
  if (izq && der) return "ambos";
  if (izq) return "izq";
  if (der) return "der";
  return null;
}

const SCROLL = /^(auto|scroll)$/;
const MARCA_TAB = "data-desborde-tabindex";

function actualizar(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const horizontal = SCROLL.test(cs.overflowX);
  const d = horizontal ? calcularDesborde(el.scrollLeft, el.scrollWidth, el.clientWidth) : null;

  if (d) el.setAttribute("data-desborde", d);
  else el.removeAttribute("data-desborde");

  // Con scroll vertical propio (encabezado fijo, `max-h-*`) Firefox conserva su barra: ahí no hay
  // forma de ocultar solo la horizontal (ver index.css).
  if (SCROLL.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1) el.setAttribute("data-scroll-y", "");
  else el.removeAttribute("data-scroll-y");

  // Columna anclada (sticky a la izquierda): el degradado izquierdo empieza donde ella termina, para
  // no desvanecer la columna que da contexto a la fila.
  if (d === "izq" || d === "ambos") {
    const th = el.querySelector<HTMLElement>(":scope > table > thead > tr > th:first-child, :scope > table > tbody > tr > td:first-child");
    const ancla = th && getComputedStyle(th).position === "sticky" ? th.offsetWidth : 0;
    el.style.setProperty("--ancla", `${ancla}px`);
  }

  if (d && !el.hasAttribute("tabindex")) {
    el.setAttribute("tabindex", "0");
    el.setAttribute(MARCA_TAB, "");
  } else if (!d && el.hasAttribute(MARCA_TAB)) {
    el.removeAttribute("tabindex");
    el.removeAttribute(MARCA_TAB);
  }
}

/** Se llama una sola vez, en `App` (`SombrasScrollTablas`). */
export function useSombrasScrollTablas() {
  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || typeof MutationObserver === "undefined") return;

    const vigilados = new Set<HTMLElement>();
    const pendientes = new Set<HTMLElement>();
    // Recorre las tablas (colección viva, barata) en lugar de consultar `:has()` en todo el documento.
    const tablas = document.getElementsByTagName("table");
    let registrarPendiente = true;
    let cuadro = 0;

    function registrar() {
      registrarPendiente = false;
      for (const el of vigilados) {
        if (!el.isConnected) {
          ro.unobserve(el);
          vigilados.delete(el);
        }
      }
      for (const t of Array.from(tablas)) {
        const el = t.parentElement;
        if (!el || t.getAttribute("role") === "grid" || vigilados.has(el)) continue;
        vigilados.add(el);
        ro.observe(el);
        ro.observe(t);
        pendientes.add(el);
      }
    }

    function programar(el?: HTMLElement) {
      if (el) pendientes.add(el);
      if (cuadro) return;
      cuadro = requestAnimationFrame(() => {
        cuadro = 0;
        if (registrarPendiente) registrar();
        pendientes.forEach((p) => p.isConnected && actualizar(p));
        pendientes.clear();
      });
    }

    const ro = new ResizeObserver((entradas) => {
      for (const e of entradas) {
        const t = e.target as HTMLElement;
        programar(t.tagName === "TABLE" ? (t.parentElement as HTMLElement) : t);
      }
    });

    const mo = new MutationObserver(() => {
      registrarPendiente = true;
      programar();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    const alDesplazar = (e: Event) => {
      const el = e.target as HTMLElement;
      if (vigilados.has(el)) programar(el);
    };
    document.addEventListener("scroll", alDesplazar, { capture: true, passive: true });

    programar();

    return () => {
      cancelAnimationFrame(cuadro);
      mo.disconnect();
      ro.disconnect();
      document.removeEventListener("scroll", alDesplazar, { capture: true });
    };
  }, []);
}
